import type { CSSProperties } from "react";

const FACE_TRANSFORMS: Record<number, string> = {
  1: "rotateX(0deg) rotateY(0deg)",
  2: "rotateX(0deg) rotateY(-90deg)",
  3: "rotateX(-90deg) rotateY(0deg)",
  4: "rotateX(90deg) rotateY(0deg)",
  5: "rotateX(0deg) rotateY(90deg)",
  6: "rotateX(0deg) rotateY(180deg)",
};

type DieStyle = CSSProperties & {
  "--die-final-transform": string;
  "--die-delay": string;
  "--die-roll-transform": string;
  "--die-spin-x": string;
  "--die-spin-y": string;
  "--die-spin-z": string;
};

type DieFaceStyle = CSSProperties & {
  "--die-texture": string;
};

export type DiePhase = "settled" | "rolling" | "settling";
export type DieVariant = "base" | "luck";

const PIP_POSITIONS: Record<number, number[]> = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

function textureUrl(variant: DieVariant, face: number): string {
  return `url("/art/dice/${variant}-${face}.png")`;
}

export function Die({
  value,
  tone,
  rolling,
  phase,
  variant = "base",
  index = 0,
}: {
  value: number;
  tone?: "win" | "lose";
  rolling?: boolean;
  phase?: DiePhase;
  variant?: DieVariant;
  index?: number;
}) {
  const safeValue = Math.max(1, Math.min(6, Math.trunc(value) || 1));
  const visualPhase = phase ?? (rolling ? "rolling" : "settled");
  const style: DieStyle = {
    "--die-final-transform": FACE_TRANSFORMS[safeValue],
    "--die-delay": `${index * 90}ms`,
    "--die-roll-transform": `rotateX(${540 + index * 97}deg) rotateY(${720 + index * 131}deg) rotateZ(${180 + index * 53}deg)`,
    "--die-spin-x": `${720 + index * 103}deg`,
    "--die-spin-y": `${900 + index * 127}deg`,
    "--die-spin-z": `${360 + index * 61}deg`,
  };

  return (
    <div
      className={`die3d-scene ${variant} ${tone ?? ""}`}
      style={style}
      aria-label={`${variant === "luck" ? "luck " : ""}dice ${safeValue}`}
    >
      <div className={`die3d ${visualPhase} ${variant} ${tone ?? ""} value-${safeValue}`}>
        {[1, 2, 3, 4, 5, 6].map((face) => (
          <div
            key={face}
            className={`die-face face-${face}`}
            style={{ "--die-texture": textureUrl(variant, face) } as DieFaceStyle}
          >
            <span className="die-pips" aria-hidden="true">
              {Array.from({ length: 9 }, (_, pip) => (
                <i key={pip} className={PIP_POSITIONS[face].includes(pip) ? "on" : ""} />
              ))}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
